import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowRight,
  Phone,
  Target,
  Users,
  Shield,
  Award,
  MapPin,
  Mail
} from 'lucide-react';

export default function AboutPage() {
  const { t } = useLanguage();

  const values = [
    {
      icon: Target,
      title: t('about.speedTitle'),
      description: t('about.speedDesc'),
    },
    {
      icon: Shield,
      title: t('about.reliabilityTitle'),
      description: t('about.reliabilityDesc'),
    },
    {
      icon: Users,
      title: t('about.partnershipTitle'),
      description: t('about.partnershipDesc'),
    },
    {
      icon: Award,
      title: t('about.expertiseTitle'),
      description: t('about.expertiseDesc'),
    },
  ];

  const borrowerTypes = [
    { title: t('borrowerTypes.fixFlip'), desc: t('borrowerTypes.fixFlipDesc') },
    { title: t('borrowerTypes.buyHold'), desc: t('borrowerTypes.buyHoldDesc') },
    { title: t('borrowerTypes.commercial'), desc: t('borrowerTypes.commercialDesc') },
    { title: t('borrowerTypes.developers'), desc: t('borrowerTypes.developersDesc') },
    { title: t('borrowerTypes.brokers'), desc: t('borrowerTypes.brokersDesc') },
    { title: t('borrowerTypes.foreign'), desc: t('borrowerTypes.foreignDesc') },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('about.pageSubtitle')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('about.pageTitle')}
            </h1>
            <p className="font-body text-slate-300 text-lg">
              {t('about.pageDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
                {t('about.missionSubtitle')}
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('about.missionTitle')}
              </h2>
              <div className="space-y-6 font-body text-slate-600 text-lg">
                <p>{t('about.missionP1')}</p>
                <p>{t('about.missionP2')}</p>
                <p>{t('about.missionP3')}</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-10">
              <div className="space-y-8">
                <div>
                  <p className="font-heading text-4xl font-bold text-amber-700">$50M+</p>
                  <p className="font-body text-slate-600">{t('about.totalFunded')}</p>
                </div>
                <div>
                  <p className="font-heading text-4xl font-bold text-amber-700">100+</p>
                  <p className="font-body text-slate-600">{t('about.successfulClosings')}</p>
                </div>
                <div>
                  <p className="font-heading text-4xl font-bold text-amber-700">5-7</p>
                  <p className="font-body text-slate-600">{t('about.avgDaysClose')}</p>
                </div>
                <div>
                  <p className="font-heading text-4xl font-bold text-amber-700">24hr</p>
                  <p className="font-body text-slate-600">{t('about.responseTime')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('about.valuesSubtitle')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('about.valuesTitle')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="card-institutional flex items-start gap-6"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-slate-900 flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-slate-600">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('about.borrowersSubtitle')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('about.borrowersTitle')}
            </h2>
            <p className="font-body text-slate-600 text-lg mb-12">
              {t('about.borrowersDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {borrowerTypes.map((item, index) => (
              <div key={index} className="border border-slate-200 p-6" data-testid={`borrower-type-${index}`}>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="font-body text-slate-600 text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
                {t('about.contactSubtitle')}
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('about.contactTitle')}
              </h2>
              <p className="font-body text-slate-600 text-lg mb-8">
                {t('about.contactDesc')}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-slate-900">{t('about.phone')}</p>
                    <a href="tel:424-303-2130" className="font-body text-amber-700 text-lg hover:text-amber-800">
                      424-303-2130
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-slate-900">{t('about.email')}</p>
                    <a href="mailto:ganninvesting@gmail.com" className="font-body text-amber-700 text-lg hover:text-amber-800">
                      ganninvesting@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-slate-900">{t('about.office')}</p>
                    <p className="font-body text-slate-600">
                      1901 Avenue of the Stars #200<br />
                      Los Angeles, CA 90067
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-900 p-10 flex flex-col justify-center">
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                {t('about.submitDealToday')}
              </h3>
              <p className="font-body text-slate-300 mb-8">
                {t('about.submitDealDesc')}
              </p>
              <Link to="/contact">
                <Button className="btn-gold rounded-none" data-testid="about-cta-btn">
                  {t('nav.getFunded')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
